<template>
  <div
    tabindex="0"
    class="search autocomplete"
    :class="((isDirty || hasItems) ? 'autocomplete_active' : '')"
  >
    <div class="autocomplete__wrapper" v-bind:class="{'loading': loading}">
      <!-- the input field -->
      <input type="text"
            class="autocomplete__input"
            placeholder=""
            autocomplete="off"
            v-model.trim="query"
            autofocus
            @keydown.down="down"
            @keydown.up="up"
            @keydown.enter="hit"
            @keydown.esc="close"
            @blur="close"
            @input="update"/>
    </div>
    <!-- the list -->
    <ul class="autocomplete__list" v-show="hasItems">
      <li class="autocomplete__list-item"
          v-for="(item, $index) in items"
          :class="activeOrSelectedClass($index)"
          @mousedown="hit"
          @mousemove="setActive($index)"
          :key="$index"
      >
          <slot name="inner-item" :item="item">
            <!-- fall back content -->
            <span v-text="item.name"></span>
          </slot>
      </li>
    </ul>
  </div>
</template>

<script>
import _ from 'lodash';
import { util } from 'vue';
import VueTypeahead from 'vue-typeahead';
import { mapGetters } from 'vuex';

export default {
  extends: VueTypeahead,
  props: {
    lookup: {
      type: Array,
      required: true,
    },
    textProp: {
      type: Function,
      required: true,
    },
    multiple: Boolean,
  },
  data() {
    return {
      // The data that would be sent by request
      // (optional)
      data: {},

      // Limit the number of items which is shown at the list
      // (optional)
      limit: 10,

      // The minimum character length needed before triggering
      // (optional)
      minChars: 2,

      // Highlight the first item in the list
      // (optional)
      selectFirst: false,

      selectedItems: [],
    };
  },

  computed: {
    src() {
      let baseUrl = `${process.env.BACKEND_API_URL}api`;

      if (this.isSuperAdmin()) {
        baseUrl += `/superadmin/${this.user.data.id}/search`;
      } else if (this.isLandlord()) {
        baseUrl += `/autocomplete/landlord/${this.user.data.selectedRole.id}`;
      } else {
        baseUrl += `/autocomplete/tenant/${this.user.data.selectedRole.id}`;
      }

      const srcList = [];

      this.lookup.forEach((lookup) => {
        srcList.push(`${baseUrl}/${lookup}`);
      });

      return srcList;
    },
    ...mapGetters({
      user: 'user/user/user',
    }),
  },

  methods: {
    fetch() {
      if (!this.$http) {
        return util.warn('You need to provide a HTTP client', this);
      }

      if (!this.src) {
        return util.warn('You need to set the `src` property', this);
      }

      const params = this.queryParamName
        ? Object.assign({ [this.queryParamName]: this.query }, this.data)
        : this.data;

      const cancel = new Promise((resolve) => { this.cancel = resolve; });

      const searchPromises = [];

      this.src.forEach((baseSrc) => {
        const src = this.queryParamName ? baseSrc : baseSrc + this.query;

        searchPromises.push(this.$http.get(src, { params }));
      });

      return Promise.race([cancel, Promise.all(searchPromises).then((result) => {
        const response = {
          data: [],
        };
        result.forEach((resp) => {
          response.data = response.data.concat(resp.data);
        });
        return response;
      })]);
    },
    // The callback function which is triggered when the user hits on an item
    // (required)
    onHit(item) {
      if (this.hasItems) {
        if (this.multiple) {
          if (_.includes(this.selectedItems, item)) {
            _.pull(this.selectedItems, item);
          } else {
            this.selectedItems.push(item);
          }
          this.query = _.map(this.selectedItems, i => this.textProp(i))
            .join(' ');
        } else {
          // enter pressed on text box
          this.query = this.textProp(item);
          this.items = [];
        }
      }
      this.$emit('query', { query: this.query, item });
      this.query = null;
    },

    // The callback function which is triggered when the response data are received
    // (optional)
    prepareResponseData(data) {
      // data = ...
      return data;
    },

    activeOrSelectedClass(index) {
      return {
        active: this.current === index,
        selected: _.includes(this.selectedItems, this.items[index]),
      };
    },

    up() {
      if (this.current > 0) {
        this.current = this.current - 1;
      } else if (this.current === -1) {
        this.current = this.items.length - 1;
      } else {
        this.current = -1;
      }

      this.updateQuery();
    },

    down() {
      if (this.current < this.items.length - 1) {
        this.current = this.current + 1;
      } else {
        this.current = -1;
      }

      this.updateQuery();
    },

    updateQuery() {
      if (!this.multiple && this.items[this.current]) {
        this.query = this.textProp(this.items[this.current]);
      }
    },

    close() {
      this.items = [];
      this.selectedItems = [];
    },
  },
};
</script>

<style lang="scss">
@import '~styles/variables.scss';
@import '~styles/_mixins.scss';

.autocomplete {
  display: inline-block;
  width: 450px;
  background-color: #fff;
  position: relative;
  &:focus,
  &:hover,
  &_active {
    @include shadow(2);
    background-color: #fff;
  }
  &.inline-search {
    @include shadow(2);
    background-color: #fff;
  }
  &_active,
  &.inline-search {
    .autocomplete__wrapper {
      border-bottom: 1px solid var(--grey-five);
      margin-bottom: 0;
    }
  }
  &__wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 20px 0 10px;
    margin-bottom: 1px;
    &:after {
      content: '';
      display: inline-block;
      width: 30px;
      height: 30px;
      background: url('~icons/search.svg') 50% 50% no-repeat;
    }
    &.loading {
      &:after {
        background-image: url('~icons/timelaps.svg');

        animation: spinner 1.5s linear infinite;
      }
    }
  }

  &__input {
    font-size: 24px;
    height: 60px;
    border: none;
    width: 100%;
    letter-spacing: 1px;
    margin-right: 20px;
  }

  &__list {
    position: absolute;
    z-index: 1000;
    min-width: 100%;
    list-style: none;
    padding: 10px;
    margin: 0;
    background-color: #fff;
    box-shadow: 0 4px 4px 0 rgba(0,0,0,0.25);
    &-item {
      line-height: 2.2em;
      cursor: pointer;
      font-size: 18px;
      color: #000;
      padding: 0 10px;
      display: flex;
      justify-content: space-between;
      &.active {
        background-color: var(--grey-four);
      }
      &.selected:after {
        content: '\e907';
        font-family: 'europark' !important;
        color: var(--brand-blue);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/Search.vue